import { DocumentNode, useMutation } from '@apollo/client';
import { Button, Grid } from '@mui/material';

import { Formik, useFormikContext } from 'formik';
import { Gateway } from 'index';
import { omit, omitBy } from 'lodash';
import omitDeep from 'omit-deep-lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import * as yup from 'yup';

import UPDATE_MUTATION from 'gql/mutations//ModulesService/updateModuleSelection.gql';
import CREATE_TEMPLATE_MUTATION from 'gql/mutations/ModuleSelectionsService/addSelectionFromTemplate.gql';
import CREATE_MUTATION from 'gql/mutations/ModulesService/addModuleSelection.gql';

import MultistepForm from 'app/components/MultistepForm';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { actions as FormErrorActions } from 'app/containers/Dashboard/FormError/slice';
import { translations } from 'locales/i18n';
import { validateForm } from 'utils/FormErrorHelper';

import '../ModuleSelectionsList/styles/ModuleSelections.scss';
import { ModuleSelection, State, SubjectType } from '../models';
import { GeneralInfo } from './components/GeneralInfo';
import { PeriodsAndExtraInfo } from './components/PeriodsAndExtraInfo';
import { moduleSelectionInitialValues, moduleSelectionValidationSchema } from './validation';

const SaveDraftButton = ({ handleSaveDraft }: { handleSaveDraft: Function }) => {
  const { t } = useTranslation();
  const { values } = useFormikContext();
  return (
    <Button
      key="submit"
      variant="contained"
      color="secondary"
      style={{ marginRight: '1em' }}
      disabled={
        !yup
          .object()
          .shape(moduleSelectionValidationSchema(t))
          .isValidSync(values, { context: { state: 'DRAFT' } })
      }
      onClick={() => handleSaveDraft(values, true)}
    >
      {t(translations.pages.moduleSelections.create.saveDraft())}
    </Button>
  );
};

interface Props {
  closeHandler: Function;
  setDisabledCancel?: Function;
  moduleSelectionToUpdate?: ModuleSelection;
  fromTemplate?: boolean;
  subjectType: SubjectType;
  refetchQueries: Array<
    string | { query: DocumentNode; variables?: any; context?: { clientName: String } }
  >;
}

export default function ModuleSelectionModal(props: Props) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [moduleSelectionQuery] = useMutation<{ addModuleSelection: ModuleSelection }, any>(
    props.fromTemplate
      ? CREATE_TEMPLATE_MUTATION
      : props.moduleSelectionToUpdate
      ? UPDATE_MUTATION
      : CREATE_MUTATION,
    {
      context: { clientName: 'federation' },
      refetchQueries: props.refetchQueries
    }
  );
  const steps = [
    t(translations.pages.moduleSelections.create.steps.generalInfo()),
    t(translations.pages.moduleSelections.create.steps.periodsAndExtraInfo())
  ];

  const handleSaveClick = async (values: any, isDraft: boolean) => {
    if (submitting) {
      return;
    }
    setSubmitting(true);

    if (props.setDisabledCancel) {
      props.setDisabledCancel(true);
    }

    if (isDraft) {
      values.state = State.Draft;
    } else if (!values.id || values.state === State.Draft) {
      values.state = State.Inactive;
    }
    const omitFields = [];
    let saveBody: any = {
      ...omit(values, omitFields)
    };

    if (values.subjectType === 'AWPM') {
      saveBody.courseId = undefined;
    }
    if (values.activeInAllCommunities === null || values.subjectType === 'FWPM') {
      saveBody.activeInAllCommunities = false;
    }

    if (values.attachments) {
      const formData = new FormData();
      values.attachments.forEach(p => {
        if (p instanceof Blob) {
          formData.append('files', p);
        }
      });
      let savedAttachments;
      if (formData.has('files')) {
        try {
          const { data } = await Gateway.post('/upload/module-selections/attachments', formData);
          savedAttachments = data;
        } catch (e) {
          dispatch(
            AlertActions.error(t(translations.pages.moduleSelections.create.alerts.saveFailed()))
          );
          setSubmitting(false);
          if (props.setDisabledCancel) {
            props.setDisabledCancel(false);
          }
          return;
        }

        saveBody.attachments = [
          ...saveBody.attachments.filter(
            existingAttachment =>
              savedAttachments.findIndex(
                newAttachment => newAttachment.originalname === existingAttachment.originalname
              ) === -1 && existingAttachment.originalname
          ),
          ...savedAttachments.map(attachment => ({
            name: attachment.name,
            originalname: attachment.originalname
          }))
        ];
      }
    }

    try {
      const variables = {
        data: {
          ...omitDeep(
            omitBy(saveBody, (v, k) => v === null || v === undefined || k === 'id'),
            '__typename',
            'id'
          ),
          startDate: saveBody?.startDate,
          endDate: saveBody?.endDate,
          dischargeByStudentsUntil: saveBody?.dischargeByStudentsUntil,
          contentVisibleFrom: saveBody?.contentVisibleFrom,
          lengthOfTheWishlist: parseInt(saveBody?.lengthOfTheWishlist),
          maxSelectedModules: parseInt(saveBody?.maxSelectedModules),
          participants:
            saveBody?.participants?.map(participant => ({
              id: parseInt(participant.id, 10)
            })) || []
        }
      };

      if (props.moduleSelectionToUpdate) {
        variables[props.fromTemplate ? 'templateId' : 'id'] = parseInt(
          props.moduleSelectionToUpdate.id,
          10
        );
      }
      const { data } = await moduleSelectionQuery({
        variables
      });
      props.closeHandler();
      dispatch(
        AlertActions.success(
          t(
            props.fromTemplate
              ? translations.pages.moduleSelections.create.alerts.createFromTemplateSuccessful()
              : !!props.moduleSelectionToUpdate
              ? translations.pages.moduleSelections.create.alerts.editSuccessful()
              : translations.pages.moduleSelections.create.alerts.saveSuccessful()
          )
        )
      );
      if (data?.addModuleSelection?.id) {
        navigate(
          `/${props.subjectType.toLowerCase()}/module-selections/${data.addModuleSelection.id}`
        );
      }
    } catch (err) {
      dispatch(
        AlertActions.error(
          t(
            props.fromTemplate
              ? translations.pages.moduleSelections.create.alerts.createFromTemplateFailed()
              : !!props.moduleSelectionToUpdate
              ? translations.pages.moduleSelections.create.alerts.editFailed()
              : translations.pages.moduleSelections.create.alerts.saveFailed()
          )
        )
      );
    }

    setSubmitting(false);
    if (props.setDisabledCancel) {
      props.setDisabledCancel(false);
    }
    props.closeHandler();
    dispatch(FormErrorActions.close());
  };
  const ModuleSelectionStep = ({ step }) => {
    switch (step) {
      case 0:
        return <GeneralInfo />;
      case 1:
        return <PeriodsAndExtraInfo />;
      default:
        return null;
    }
  };
  const initialValues =
    props.fromTemplate && props.moduleSelectionToUpdate
      ? {
          ...props.moduleSelectionToUpdate,
          startDate: null,
          endDate: null,
          contentVisibleFrom: null,
          dischargeByStudentsUntil: null
        }
      : props.moduleSelectionToUpdate;

  const showSaveAsDraftButton: boolean =
    props.moduleSelectionToUpdate?.state === undefined ||
    props.moduleSelectionToUpdate.state === State.Draft ||
    props.moduleSelectionToUpdate.state === State.Inactive;
  return (
    <Grid item className="moduleModal">
      <Formik
        initialValues={moduleSelectionInitialValues({
          subjectType: props.subjectType,
          moduleSelectionToUpdate: initialValues
        })}
        validationSchema={yup.object().shape(moduleSelectionValidationSchema(t))}
        onSubmit={values => handleSaveClick(values, false)}
        validate={values => {
          return validateForm(
            values,
            yup.object().shape(moduleSelectionValidationSchema(t)),
            dispatch
          );
        }}
      >
        <MultistepForm
          submitting={submitting}
          stepValidator={() => true}
          steps={steps}
          onCancel={props.closeHandler}
          isStepperClickable={true}
          additionalSave={
            showSaveAsDraftButton ? (
              <SaveDraftButton handleSaveDraft={handleSaveClick} />
            ) : undefined
          }
        >
          {step => (
            <Grid container>
              <ModuleSelectionStep step={step} />
            </Grid>
          )}
        </MultistepForm>
      </Formik>
    </Grid>
  );
}
